import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingCart, Trash2, Minus, Plus, Check, Copy, Wallet, ExternalLink, ArrowLeft,
  Sparkles, Loader2, FileText, Crown, Download, ShieldCheck,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useCart, CART_CATALOG } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  hasEthereumProvider, connectWallet, ensurePolygon, sendUsdcPayment,
} from '../../lib/wallet';

const PAYPAL_CLIENT_ID = 'BAAie7-_LGGsVivHXw98Y7puC1wtLO2xkBJwkn9PU0ib2iVyPGzn6o2NiEVy5fPyGdiNyS82VYUWH7EoCI';
const USDC_POLYGON = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
const PLATFORM_WALLET_FALLBACK = '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb8';

function loadPaypalSdk() {
  return new Promise((resolve, reject) => {
    if (window.paypal && window.paypal.Buttons) return resolve(window.paypal);
    const existing = document.getElementById('paypal-sdk-smart');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.paypal));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'paypal-sdk-smart';
    s.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=buttons&enable-funding=venmo&currency=USD`;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export default function CartPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { items, removeItem, updateQuantity, clear, totals } = useCart();
  const [paymentMethod, setPaymentMethod] = useState('paypal');

  // Crypto state
  const [txHash, setTxHash] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [submittingCrypto, setSubmittingCrypto] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState('');
  const [walletBusy, setWalletBusy] = useState(false);
  const [platformWallet, setPlatformWallet] = useState({ address: PLATFORM_WALLET_FALLBACK, label: '' });
  const hasWallet = hasEthereumProvider();

  // Fetch the live platform wallet + the user's saved address on mount
  useEffect(() => {
    (async () => {
      try {
        const [pw, mw] = await Promise.all([
          api.get('/platform-wallet').catch(() => null),
          api.get('/me/wallet').catch(() => null),
        ]);
        if (pw?.data?.address) setPlatformWallet({ address: pw.data.address, label: pw.data.label || '' });
        if (mw?.data?.wallet_address) setWalletAddress(mw.data.wallet_address);
      } catch (_) { /* non-critical */ }
    })();
  }, []);

  const PLATFORM_WALLET = platformWallet.address;

  // PayPal state
  const paypalRef = useRef(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [paypalProcessing, setPaypalProcessing] = useState(false);

  // Render PayPal Smart Button when method selected and items present
  useEffect(() => {
    if (paymentMethod !== 'paypal' || items.length === 0 || !user) return;

    let cancelled = false;
    let buttonsInstance = null;

    (async () => {
      try {
        await loadPaypalSdk();
        if (cancelled || !paypalRef.current) return;
        paypalRef.current.innerHTML = '';

        buttonsInstance = window.paypal.Buttons({
          style: { layout: 'vertical', color: 'blue', shape: 'rect', label: 'paypal' },

          createOrder: (_data, actions) => {
            return actions.order.create({
              purchase_units: [{
                description: items
                  .map((it) => `${(CART_CATALOG[it.service_id]?.name || it.service_id)}${it.entity_name ? ` (${it.entity_name})` : ''} x${it.quantity}`)
                  .join(', ')
                  .slice(0, 127),
                amount: {
                  value: totals.totalUsd.toFixed(2),
                  currency_code: 'USD',
                  breakdown: {
                    item_total: { currency_code: 'USD', value: totals.totalUsd.toFixed(2) },
                  },
                },
                items: items.map((it) => {
                  const cat = CART_CATALOG[it.service_id];
                  const isDynamic = cat?.dynamic;
                  const unitPrice = isDynamic ? (it.price_usd || 0) : cat.price;
                  const labelBase = cat?.name || it.service_id;
                  const label = it.entity_name ? `${labelBase} - ${it.entity_name}` : labelBase;
                  return {
                    name: label.slice(0, 127),
                    quantity: String(it.quantity),
                    unit_amount: { currency_code: 'USD', value: unitPrice.toFixed(2) },
                    category: 'DIGITAL_GOODS',
                  };
                }),
              }],
            });
          },

          onApprove: async (data, actions) => {
            setPaypalProcessing(true);
            try {
              // Capture on PayPal side first (so order is finalized)
              const capture = await actions.order.capture();
              const payerEmail = capture?.payer?.email_address;

              // Notify backend to activate subscription / record add-ons
              const res = await api.post('/cart/checkout-paypal', {
                items: items.map(({ service_id, quantity, entity_id }) => ({ service_id, quantity, entity_id })),
                paypal_order_id: data.orderID,
                payer_email: payerEmail,
              });

              const tier = res.data.tier_activated;
              if (tier) {
                toast.success(res.data.message);
                // Refresh user profile so SubscriptionGate updates
                try {
                  const me = await api.get('/auth/me');
                  if (me.data && updateUser) updateUser(me.data);
                } catch (_) { /* non-critical */ }
              } else {
                toast.success(res.data.message || 'Payment complete');
              }
              clear();
              navigate('/payments/success', {
                state: {
                  orderId: res.data.order_id,
                  tier,
                  items: res.data.items,
                  totalUsd: res.data.total_usd,
                  method: 'paypal',
                },
              });
            } catch (e) {
              console.error('Cart PayPal checkout error:', e);
              toast.error(e.response?.data?.detail || 'Payment captured but activation failed. Contact support.');
            } finally {
              setPaypalProcessing(false);
            }
          },

          onError: (err) => {
            console.error('PayPal error:', err);
            toast.error('PayPal payment failed. Please try again or use crypto.');
            setPaypalProcessing(false);
          },

          onCancel: () => {
            toast.info('Payment cancelled');
            setPaypalProcessing(false);
          },
        });

        if (buttonsInstance.isEligible()) {
          await buttonsInstance.render(paypalRef.current);
          if (!cancelled) setPaypalReady(true);
        }
      } catch (e) {
        console.error('PayPal SDK load error:', e);
      }
    })();

    return () => {
      cancelled = true;
      try { buttonsInstance && buttonsInstance.close && buttonsInstance.close(); } catch (_) { /* ignore */ }
      if (paypalRef.current) paypalRef.current.innerHTML = '';
      setPaypalReady(false);
    };
    // Re-render if items/total changes so PayPal picks up the new amount
  }, [paymentMethod, items, totals.totalUsd, user, clear, navigate, updateUser]);

  const copyAddress = () => {
    navigator.clipboard.writeText(PLATFORM_WALLET);
    setCopied(true);
    toast.success('Wallet address copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConnectWallet = async () => {
    setWalletBusy(true);
    try {
      const addr = await connectWallet();
      setConnectedWallet(addr);
      setWalletAddress(addr);
      toast.success('Wallet connected');
    } catch (e) {
      toast.error(e.message || 'Failed to connect wallet');
    } finally {
      setWalletBusy(false);
    }
  };

  const handleWalletPayAndSubmit = async () => {
    setWalletBusy(true);
    try {
      const from = connectedWallet || await connectWallet();
      setConnectedWallet(from);
      setWalletAddress(from);

      toast.info('Switching to Polygon…');
      await ensurePolygon();

      toast.info('Confirm the USDC transfer in your wallet');
      const hash = await sendUsdcPayment({
        from,
        to: PLATFORM_WALLET,
        amountUsdc: totals.totalUsdc,
      });

      setTxHash(hash);
      toast.success('Transaction submitted — finalizing with CorpCraft…');

      // Auto-submit checkout now that we have a hash
      setSubmittingCrypto(true);
      const res = await api.post('/cart/checkout-crypto', {
        items: items.map(({ service_id, quantity, entity_id, formation_inputs }) => ({
          service_id, quantity, entity_id, formation_inputs,
        })),
        tx_hash: hash,
        wallet_address: from,
        amount_usdc: totals.totalUsdc,
      });

      const tier = res.data.tier_activated;
      toast.success(res.data.message);
      if (tier) {
        try {
          const me = await api.get('/auth/me');
          if (me.data && updateUser) updateUser(me.data);
        } catch (_) { /* non-critical */ }
      }
      clear();
      navigate('/payments/success', {
        state: {
          orderId: res.data.order_id,
          tier,
          items: res.data.items,
          totalUsdc: res.data.total_usdc,
          method: 'crypto',
          txHash: hash,
        },
      });
    } catch (e) {
      console.error('Wallet pay error:', e);
      const msg = e.response?.data?.detail || e.message || 'Wallet payment failed';
      toast.error(typeof msg === 'string' ? msg : 'Wallet payment failed');
    } finally {
      setWalletBusy(false);
      setSubmittingCrypto(false);
    }
  };

  const handleCryptoSubmit = async (e) => {
    e.preventDefault();
    if (txHash.length < 30) {
      toast.error('Please enter a valid transaction hash');
      return;
    }
    setSubmittingCrypto(true);
    try {
      const res = await api.post('/cart/checkout-crypto', {
        items: items.map(({ service_id, quantity, entity_id, formation_inputs }) => ({
          service_id, quantity, entity_id, formation_inputs,
        })),
        tx_hash: txHash.trim(),
        wallet_address: walletAddress.trim() || null,
        amount_usdc: totals.totalUsdc,
      });

      const tier = res.data.tier_activated;
      toast.success(res.data.message);
      if (tier) {
        try {
          const me = await api.get('/auth/me');
          if (me.data && updateUser) updateUser(me.data);
        } catch (_) { /* non-critical */ }
      }
      clear();
      navigate('/payments/success', {
        state: {
          orderId: res.data.order_id,
          tier,
          items: res.data.items,
          totalUsdc: res.data.total_usdc,
          method: 'crypto',
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmittingCrypto(false);
    }
  };

  // Auth gate
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4" data-testid="cart-auth-gate">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-blue-400" />
          <h1 className="text-2xl font-bold mb-2">Sign in to checkout</h1>
          <p className="text-slate-400 mb-6">Your cart is saved. Sign in to complete your purchase.</p>
          <Button onClick={() => navigate('/login')} className="w-full gradient-accent" data-testid="cart-signin-btn">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // Empty cart
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 px-4 py-12">
        <div className="max-w-2xl mx-auto text-center" data-testid="cart-empty">
          <button onClick={() => navigate('/pricing')} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8" data-testid="cart-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to pricing
          </button>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-12">
            <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-slate-600" />
            <h1 className="text-3xl font-bold mb-2">Your cart is empty</h1>
            <p className="text-slate-400 mb-8">Pick a plan and add-ons to get started.</p>
            <Button onClick={() => navigate('/pricing')} className="gradient-accent" data-testid="cart-go-pricing-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Browse Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const tierInCart = totals.tierInCart;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12" data-testid="cart-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/pricing')} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6" data-testid="cart-back-btn">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </button>

        <h1 className="text-3xl font-bold mb-8 font-['Outfit']">Your Cart</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Line items */}
          <div className="lg:col-span-2 space-y-3">
            {items.map((it) => {
              const cat = CART_CATALOG[it.service_id];
              if (!cat) return null;
              const isDynamic = cat.dynamic;
              const isTier = cat.type === 'tier';
              const isStateFiling = cat.type === 'state_filing';
              const isFormation = cat.type === 'formation_package';
              const priceUsd = isDynamic ? (it.price_usd || 0) : cat.price;
              const priceUsdc = isDynamic ? (it.price_usdc || 0) : cat.priceUsdc;
              const subUsd = (priceUsd * it.quantity).toFixed(2);
              const subUsdc = (priceUsdc * it.quantity).toFixed(2);
              const lineKey = isStateFiling ? `state_filing_fee_${it.entity_id}` : it.service_id;
              const displayName = isStateFiling
                ? `State filing — ${it.entity_name || cat.name}`
                : (isFormation ? (it.label || 'Formation Package') : cat.name);
              return (
                <div
                  key={lineKey}
                  data-testid={`cart-line-${lineKey}`}
                  className="bg-slate-900 border border-slate-800 rounded-xl p-5 flex items-center gap-4"
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isTier ? 'bg-blue-500/20 text-blue-400' :
                    isStateFiling ? 'bg-amber-500/20 text-amber-400' :
                    isFormation ? 'bg-purple-500/20 text-purple-400' :
                    'bg-emerald-500/20 text-emerald-400'
                  }`}>
                    {isTier ? <Sparkles className="w-5 h-5" /> :
                     isStateFiling ? <FileText className="w-5 h-5" /> :
                     isFormation ? <Crown className="w-5 h-5" /> :
                     <Check className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{displayName}</div>
                    <div className="text-sm text-slate-400">
                      ${priceUsd.toFixed(2)}{cat.billing || ''} <span className="text-slate-600">·</span> ${priceUsdc.toFixed(2)} USDC
                      {isTier && <span className="ml-2 text-blue-400">subscription</span>}
                      {isStateFiling && <span className="ml-2 text-amber-400">{it.state} state filing</span>}
                      {isFormation && (
                        <span className="ml-2 text-purple-400">
                          {it.entity_credits} entity credit{it.entity_credits === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                    {isFormation && it.a_la_carte_savings_usd > 0 && (
                      <div
                        data-testid={`cart-savings-${lineKey}`}
                        className="mt-2 inline-flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-xs rounded-full px-2.5 py-1"
                      >
                        <Sparkles className="w-3 h-3" />
                        You save <span className="font-bold">${it.a_la_carte_savings_usd.toLocaleString()}</span> vs. {it.entity_credits} single-entity packages
                      </div>
                    )}
                  </div>

                  {!isTier && !isStateFiling && !isFormation ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => updateQuantity(it.service_id, it.quantity - 1)}
                        data-testid={`cart-qty-down-${it.service_id}`}
                        className="h-8 w-8"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-6 text-center font-mono" data-testid={`cart-qty-${it.service_id}`}>{it.quantity}</span>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => updateQuantity(it.service_id, it.quantity + 1)}
                        data-testid={`cart-qty-up-${it.service_id}`}
                        className="h-8 w-8"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="text-xs text-slate-500 italic">qty 1</div>
                  )}

                  <div className="text-right hidden sm:block">
                    <div className="font-semibold">${subUsd}</div>
                    <div className="text-xs text-slate-500">${subUsdc} USDC</div>
                  </div>

                  <Button
                    variant="ghost" size="icon"
                    onClick={() => removeItem(it.service_id, it.entity_id)}
                    data-testid={`cart-remove-${lineKey}`}
                    className="text-slate-500 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Summary + checkout */}
          <div className="space-y-4">
            {(() => {
              const bundleSavings = items.reduce((sum, it) => sum + (it.a_la_carte_savings_usd || 0), 0);
              if (bundleSavings <= 0) return null;
              return (
                <div
                  data-testid="cart-bundle-savings-banner"
                  className="bg-gradient-to-br from-emerald-500/15 to-teal-500/10 border border-emerald-500/40 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-emerald-300" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-emerald-100">
                        You're saving <span className="text-emerald-300">${bundleSavings.toLocaleString()}</span> vs. a la carte
                      </div>
                      <div className="text-xs text-emerald-200/80 mt-0.5">
                        Buying a bundle is cheaper than adding single-entity packages one at a time.
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 sticky top-6" data-testid="cart-summary">
              <h2 className="font-bold text-lg mb-4">Order Summary</h2>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between"><span className="text-slate-400">Items</span><span data-testid="cart-summary-count">{totals.itemCount}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Subtotal (USD)</span><span data-testid="cart-summary-usd">${totals.totalUsd.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Subtotal (USDC, -8%)</span><span className="text-emerald-400" data-testid="cart-summary-usdc">${totals.totalUsdc.toFixed(2)}</span></div>
                {totals.savingsUsd > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-slate-500">Crypto savings</span><span className="text-emerald-400">−${totals.savingsUsd.toFixed(2)}</span></div>
                )}
              </div>

              <div className="border-t border-slate-800 pt-4 mb-4">
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-slate-400">Total</span>
                  <span className="text-2xl font-bold" data-testid="cart-total">
                    {paymentMethod === 'crypto' ? `$${totals.totalUsdc.toFixed(2)}` : `$${totals.totalUsd.toFixed(2)}`}
                  </span>
                </div>
                <div className="text-right text-xs text-slate-500">{paymentMethod === 'crypto' ? 'USDC' : 'USD'}</div>
              </div>

              {/* Method toggle */}
              <div className="grid grid-cols-2 gap-2 mb-5" data-testid="cart-payment-toggle">
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  data-testid="cart-method-paypal"
                  className={`py-3 rounded-lg border-2 text-sm font-medium transition ${
                    paymentMethod === 'paypal' ? 'border-blue-500 bg-blue-500/10 text-blue-300' : 'border-slate-800 hover:border-slate-700 text-slate-400'
                  }`}
                >
                  PayPal
                </button>
                <button
                  onClick={() => setPaymentMethod('crypto')}
                  data-testid="cart-method-crypto"
                  className={`py-3 rounded-lg border-2 text-sm font-medium transition ${
                    paymentMethod === 'crypto' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' : 'border-slate-800 hover:border-slate-700 text-slate-400'
                  }`}
                >
                  USDC <span className="text-emerald-400 text-xs">−8%</span>
                </button>
              </div>

              {/* PayPal */}
              {paymentMethod === 'paypal' && (
                <div data-testid="cart-paypal-area">
                  <div ref={paypalRef} id="paypal-button-container" />
                  {!paypalReady && (
                    <div className="text-center text-slate-500 text-sm py-4">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading PayPal…
                    </div>
                  )}
                  {paypalProcessing && (
                    <div className="mt-3 text-center text-blue-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Activating your subscription…
                    </div>
                  )}
                  {tierInCart && (
                    <p className="text-xs text-slate-500 text-center mt-3">
                      Your <span className="text-blue-300">{CART_CATALOG[tierInCart].name}</span> activates instantly after PayPal confirms.
                    </p>
                  )}
                </div>
              )}

              {/* Crypto */}
              {paymentMethod === 'crypto' && (
                <div className="space-y-4" data-testid="cart-crypto-area">
                  {/* --- No-wallet onboarding --- */}
                  {!hasWallet && (
                    <div
                      data-testid="no-wallet-onboarding"
                      className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-4 h-4 text-amber-400" />
                        <span className="font-semibold text-amber-200 text-sm">No crypto wallet yet?</span>
                      </div>
                      <p className="text-xs text-amber-100/80 mb-3">
                        You need a wallet like MetaMask to hold USDC. It takes about 2 minutes.
                      </p>
                      <ol className="text-xs text-amber-100/80 list-decimal list-inside space-y-1 mb-3">
                        <li>Install the MetaMask browser extension.</li>
                        <li>Create a new wallet & save your secret recovery phrase.</li>
                        <li>Buy or transfer USDC on Polygon, then return here.</li>
                      </ol>
                      <div className="grid grid-cols-2 gap-2">
                        <a
                          href="https://metamask.io/download/"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="install-metamask-link"
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-100 text-xs font-medium hover:bg-amber-500/30"
                        >
                          <Download className="w-3.5 h-3.5" /> Install MetaMask
                        </a>
                        <a
                          href="https://metamask.io/faqs/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-700 text-slate-300 text-xs font-medium hover:bg-slate-800"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Learn more
                        </a>
                      </div>
                      <p className="text-[10px] text-amber-200/60 mt-3">
                        Already have a wallet on mobile? Open this page inside MetaMask's built-in browser.
                      </p>
                    </div>
                  )}

                  {/* --- 1-click connect & pay --- */}
                  {hasWallet && (
                    <div
                      data-testid="wallet-connect-card"
                      className="bg-slate-950 border border-emerald-500/30 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span className="font-semibold text-sm">Pay with your wallet</span>
                      </div>
                      {connectedWallet ? (
                        <div className="text-xs text-slate-400 mb-2 font-mono break-all">
                          Connected: {connectedWallet.slice(0, 6)}…{connectedWallet.slice(-4)}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 mb-3">
                          Connect MetaMask and we'll send the USDC transfer on Polygon in one click.
                        </p>
                      )}
                      <Button
                        type="button"
                        onClick={handleWalletPayAndSubmit}
                        disabled={walletBusy || submittingCrypto}
                        className="w-full gradient-accent"
                        data-testid="wallet-pay-btn"
                      >
                        {walletBusy ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Waiting for wallet…</>
                        ) : connectedWallet ? (
                          <>Pay {totals.totalUsdc.toFixed(2)} USDC from wallet</>
                        ) : (
                          <><Wallet className="w-4 h-4 mr-2" /> Connect & Pay {totals.totalUsdc.toFixed(2)} USDC</>
                        )}
                      </Button>
                      {!connectedWallet && (
                        <button
                          type="button"
                          onClick={handleConnectWallet}
                          disabled={walletBusy}
                          data-testid="wallet-connect-only-btn"
                          className="text-xs text-slate-500 hover:text-slate-300 mt-2 block mx-auto"
                        >
                          Just connect (don't pay yet)
                        </button>
                      )}
                    </div>
                  )}

                  {/* --- Manual tx-hash fallback --- */}
                  <div className="relative">
                    <div className="absolute inset-x-0 -top-2 flex justify-center">
                      <span className="bg-slate-900 px-2 text-[10px] uppercase tracking-widest text-slate-500">
                        or send manually
                      </span>
                    </div>
                    <form onSubmit={handleCryptoSubmit} className="pt-3 space-y-3" data-testid="cart-crypto-form">
                      <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
                        <Label className="text-xs text-slate-400">Send exactly</Label>
                        <div className="text-2xl font-bold text-emerald-400 my-1">{totals.totalUsdc.toFixed(2)} USDC</div>
                        <div className="text-xs text-slate-500">on Polygon network to:</div>
                        <div className="flex gap-2 mt-2">
                          <Input value={PLATFORM_WALLET} readOnly className="font-mono text-xs h-9" data-testid="cart-wallet-address" />
                          <Button type="button" variant="outline" size="icon" onClick={copyAddress} className="h-9 w-9" data-testid="cart-copy-wallet">
                            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="tx-hash" className="text-xs">Transaction hash *</Label>
                        <Input
                          id="tx-hash"
                          value={txHash}
                          onChange={(e) => setTxHash(e.target.value)}
                          placeholder="0x..."
                          className="font-mono text-xs"
                          data-testid="cart-tx-hash"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="wallet-from" className="text-xs">Your wallet (optional)</Label>
                        <Input
                          id="wallet-from"
                          value={walletAddress}
                          onChange={(e) => setWalletAddress(e.target.value)}
                          placeholder="0x..."
                          className="font-mono text-xs"
                          data-testid="cart-wallet-from"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submittingCrypto || txHash.length < 30}
                        className="w-full"
                        variant="outline"
                        data-testid="cart-submit-crypto-btn"
                      >
                        {submittingCrypto ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting…</>
                        ) : (
                          <>Submit manual payment ({totals.totalUsdc.toFixed(2)} USDC)</>
                        )}
                      </Button>

                      <a
                        href={`https://polygonscan.com/address/${PLATFORM_WALLET}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mt-2"
                      >
                        <ExternalLink className="w-3 h-3" /> Verify wallet on PolygonScan
                      </a>
                      <div className="text-[10px] text-slate-600 break-all">USDC contract: {USDC_POLYGON}</div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
